import { existsSync, readFileSync } from "node:fs";
import { dirname, join, normalize, relative } from "node:path";
import { globSync } from "glob";
import type { EntryPoint, ServiceGraph, ServiceGraphEdge, ServiceGraphNode } from "../types.js";

const SERVICE_PATTERNS = [
  "src/services/**/*.{ts,tsx,js,jsx}",
  "src/providers/**/*.{ts,tsx,js,jsx}",
  "src/handlers/**/*.{ts,tsx,js,jsx}",
  "src/controllers/**/*.{ts,tsx,js,jsx}",
  "src/routes/**/*.{ts,tsx,js,jsx}",
  "src/api/**/*.{ts,tsx,js,jsx}",
  "app/api/**/*.{ts,tsx,js,jsx}",
];

const IMPORT_PATTERNS = [
  /import\s+(?:type\s+)?(?:[^"'`]+?\s+from\s+)?["'`]([^"'`]+)["'`]/g,
  /export\s+[^"'`]+?\s+from\s+["'`]([^"'`]+)["'`]/g,
  /require\(["'`]([^"'`]+)["'`]\)/g,
];

const RESOLUTION_SUFFIXES = [
  "",
  ".ts",
  ".tsx",
  ".js",
  ".jsx",
  "/index.ts",
  "/index.tsx",
  "/index.js",
  "/index.jsx",
];

export function scanServiceGraph(projectRoot: string, entryPoints: EntryPoint[]): ServiceGraph {
  const entryPaths = entryPoints
    .filter((entry) => entry.type === "main")
    .map((entry) => entry.path);

  const servicePaths = SERVICE_PATTERNS.flatMap((pattern) =>
    globSync(pattern, {
      cwd: projectRoot,
      ignore: ["node_modules/**", "dist/**", "build/**", ".git/**"],
    })
  );

  const allPaths = [...new Set([...entryPaths, ...servicePaths])].sort();
  const nodeMap = new Map<string, ServiceGraphNode>();
  for (const path of allPaths) {
    nodeMap.set(path, {
      path,
      kind: entryPaths.includes(path) ? "entry" : "service",
    });
  }

  const edges: ServiceGraphEdge[] = [];
  const edgeKeys = new Set<string>();

  for (const path of allPaths) {
    const absolutePath = join(projectRoot, path);
    if (!existsSync(absolutePath)) continue;

    let content = "";
    try {
      content = readFileSync(absolutePath, "utf8");
    } catch {
      continue;
    }

    for (const specifier of extractImportSpecifiers(content)) {
      if (!specifier.startsWith(".")) continue;
      const resolved = resolveRelativeImport(projectRoot, path, specifier);
      if (!resolved || !nodeMap.has(resolved)) continue;

      const key = `${path}->${resolved}`;
      if (edgeKeys.has(key) || path === resolved) continue;
      edgeKeys.add(key);
      edges.push({ from: path, to: resolved });
    }
  }

  return {
    nodes: [...nodeMap.values()],
    edges: edges.sort((a, b) => `${a.from}:${a.to}`.localeCompare(`${b.from}:${b.to}`)),
  };
}

function extractImportSpecifiers(content: string): string[] {
  const values: string[] = [];
  for (const pattern of IMPORT_PATTERNS) {
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(content)) !== null) {
      values.push(match[1]);
    }
    pattern.lastIndex = 0;
  }
  return values;
}

function resolveRelativeImport(
  projectRoot: string,
  fromPath: string,
  specifier: string
): string | null {
  const fromDir = dirname(join(projectRoot, fromPath));
  const normalizedBase = normalize(join(fromDir, specifier));

  for (const suffix of RESOLUTION_SUFFIXES) {
    const candidate = normalize(`${normalizedBase}${suffix}`);
    if (!existsSync(candidate)) continue;
    return relative(projectRoot, candidate);
  }

  return null;
}
